<template>
	<validation-observer
		tag="div"
		class="cassie-vertical-md"
	>
		<SectionCard
			:collapsible="!forbiddenAuthType && contactDetailsFields.length === 0"
			:collapsed="!showPII"
		>
			<template #title>
				Personal Information Fields
				<v-tooltip
					right
				>
					<template #activator="{ on }">
						<v-icon
							right
							v-on="on"
							@click.stop="piiFieldsModalOpen = true"
						>
							mdi-information
						</v-icon>
					</template>
					<span>
						Click to expand
					</span>
				</v-tooltip>
			</template>
			<template #subtitle>
				Enabling this option will allow you to add fields to a Preference Page to allow a Data Subject to view or update personal information.

				<v-row
					v-if="forbiddenAuthType"
					class="mt-2"
					dense
				>
					<Message type="warning">
						Your selected Authentication Type cannot be used when Personal Information Fields are enabled. Please select one of the following Authentication Types on the Details Tab.
						<ul>
							<li>
								Confirm Value
							</li>
							<li>
								Prove Email
							</li>
							<li>
								Provide and Prove Email
							</li>
						</ul>
					</Message>
				</v-row>
			</template>
			<template #body>
				<div class="cassie-vertical-md">
					<v-row
						v-if="forbiddenAuthType && contactDetailsFields.length >= 1"
						dense
					>
						<v-col cols="4">
							<DeleteButton @click="removeAllExistingFields">
								Remove Existing Fields
							</DeleteButton>
						</v-col>
					</v-row>
					<v-row
						v-if="userFullPermissions && !forbiddenAuthType"
						dense
					>
						<v-col cols="6">
							<Dropdown
								v-model="selectedContactDetailsFields"
								:search-input.sync="searchInput"
								label="Fields"
								searchable
								:items="availableContactDetailsFields"
								custom-sort
								multiple
								select-all
								@change="updateSelectAllChecked"
							>
								<template
									v-if="showSelectAll"
									#prepend-item
								>
									<v-list-item
										dense
										@click="toggleSelectAll"
									>
										<v-checkbox
											v-model="selectAllChecked"
											dense
											@click="toggleSelectAll"
										/>
										<v-list-item-title class="pb-2">
											Select All
										</v-list-item-title>
									</v-list-item>
									<v-divider />
								</template>
							</Dropdown>
						</v-col>
						<v-col cols="3">
							<PrimaryActionButton
								@click="addContactDetailsFields"
							>
								Add
							</PrimaryActionButton>
						</v-col>
					</v-row>
					<DataTable
						v-if="!forbiddenAuthType"
						:headers="tableHeaders"
						:items="contactDetailsFields"
						:items-per-page="50"
						hide-default-footer
					>
						<template #item.displayName="{ item, index }">
							<validation-provider
								#default="{ errors }"
								rules="required"
								mode="aggressive"
							>
								<TextField
									:value="item.displayName"
									label="Display Name *"
									:disabled="!userFullPermissions || forbiddenAuthType"
									:error-messages="errors"
									small
									:rules="{required: true, max: 200}"
									class="my-1"
									@input="updateContactDetailsRow(index, 'displayName', $event)"
								/>
							</validation-provider>
						</template>
						<template #item.readOnly="{ item, index }">
							<Toggle
								:value.sync="item.readOnly"
								:disabled="!userFullPermissions || item.mandatory || readOnlyField(item) || forbiddenAuthType"
								@update:value="updateContactDetailsRow(index, 'readOnly', $event)"
							/>
						</template>
						<template #item.mandatory="{ item, index }">
							<Toggle
								:value.sync="item.mandatory"
								:disabled="!userFullPermissions || item.readOnly || forbiddenAuthType"
								@update:value="updateContactDetailsRow(index, 'mandatory', $event)"
							/>
						</template>
						<template #item.displayOrder="{ index }">
							{{ index + 1 }}
						</template>
						<template #item.action="{ index, item }">
							<div class="d-flex align-center justify-end">
								<IconButton
									v-if="index !== 0 && userFullPermissions && !forbiddenAuthType"
									@click="moveFieldUp(index)"
								>
									mdi-arrow-up
								</IconButton>
								<IconButton
									v-if="index !== contactDetailsFields.length - 1 && userFullPermissions && !forbiddenAuthType"
									@click="moveFieldDown(index)"
								>
									mdi-arrow-down
								</IconButton>
								<v-menu
									bottom
									left
									offset-y
								>
									<template #activator="{ on, attrs }">
										<v-btn
											icon
											small
											v-bind="attrs"
											v-on="on"
											@click.stop
										>
											<v-icon small>
												mdi-dots-horizontal
											</v-icon>
										</v-btn>
									</template>
									<v-list dense>
										<v-list-item @click="restoreDefaultDisplayText(index, item)">
											<v-list-item-content>
												<v-list-item-title>
													Restore Default Display Text
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
										<v-list-item
											v-if="userFullPermissions"
											@click="removeContactDetailsField(index)"
										>
											<v-list-item-content>
												<v-list-item-title>
													Remove Field
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</template>
					</DataTable>
				</div>
			</template>
		</SectionCard>
		<PiiFieldsHelperModal
			v-if="piiFieldsModalOpen"
			@close="piiFieldsModalOpen = false"
		/>
	</validation-observer>
</template>
<script>
import SectionCard from '../../../../../../shared/components/section-card.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import DeleteButton from '../../../../../../shared/components/delete-button.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import IconButton from '../../../../../../shared/components/icon-button.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import Toggle from '../../../../../../shared/components/toggle.vue'
import Message from '../../../../../../shared/components/message.vue'
import PiiFieldsHelperModal from './pii-fields-helper-modal.vue'
import { DIRECT_AUTHENTICATION_TYPE, PROVIDE_EMAIL_AUTHENTICATION_TYPE } from '../link-authentication-types.js'
export default {
	components: {
		SectionCard,
		Dropdown,
		PrimaryActionButton,
		DataTable,
		IconButton,
		TextField,
		Toggle,
		Message,
		DeleteButton,
		PiiFieldsHelperModal
	},
	props: {
		contactDetailsFields: {
			type: Array,
			default: () => []
		},
		linkAuthTypes: {
			type: Array,
			default: () => []
		},
		lookupDataContactFields: {
			type: Array,
			default: () => []
		},
		authenticationDetails: Object,
		userFullPermissions: Boolean
	},
	data () {
		return {
			DIRECT_AUTHENTICATION_TYPE,
			PROVIDE_EMAIL_AUTHENTICATION_TYPE,
			showPII: false,
			selectedContactDetailsFields: [],
			selectAllChecked: false,
			piiFieldsModalOpen: false,
			searchInput: null
		}
	},
	computed: {
		availableContactDetailsFields () {
			const CRC_FIELD = 0
			const contactDetailsFields = this.lookupDataContactFields.filter(({ id }) => id !== CRC_FIELD)
			return contactDetailsFields.map(({ id, defaultDisplayName }) => ({
				value: id, text: defaultDisplayName
			})).filter(({ value }) => {
				return !this.contactDetailsFields.find(({ contactFieldId }) => contactFieldId === value)
			})
		},
		showSelectAll () {
			return this.availableContactDetailsFields.length > 1
		},
		tableHeaders () {
			return [
				{ text: 'Display Name', value: 'displayName', width: '30%' },
				{ text: 'Read Only', value: 'readOnly', width: '23%' },
				{ text: 'Mandatory', value: 'mandatory', width: '23%' },
				{ text: 'Display Order', value: 'displayOrder', width: '24%' },
				{ text: 'Action', value: 'action', width: '120px', align: 'right', sortable: false }
			]
		},
		forbiddenAuthType () {
			return this.authenticationDetails.authTypeId === DIRECT_AUTHENTICATION_TYPE ||
				this.authenticationDetails.authTypeId === PROVIDE_EMAIL_AUTHENTICATION_TYPE
		}
	},
	created () {
		if (this.contactDetailsFields.length >= 1) {
			this.showPII = true
		}
	},
	methods: {
		toggleSelectAll () {
			if (this.selectedContactDetailsFields.length === this.availableContactDetailsFields.length) {
				this.selectAllChecked = false
				this.selectedContactDetailsFields = []
			} else {
				this.selectAllChecked = true
				if (this.searchInput) {
					this.selectedContactDetailsFields = this.availableContactDetailsFields
						.filter(({ text }) => text.toLowerCase().includes(this.searchInput.toLowerCase()))
						.map(({ value }) => value)
				} else {
					this.selectedContactDetailsFields = this.availableContactDetailsFields.map(contactField => {
						return contactField.value
					})
				}
			}
		},
		updateSelectAllChecked () {
			const availableFields = [...this.availableContactDetailsFields]
			const filteredFields = this.searchInput
				? availableFields.filter(field => field.text.toLowerCase().includes(this.searchInput.toLowerCase()))
				: availableFields
			this.selectAllChecked = this.selectedContactDetailsFields.length === filteredFields.length
		},
		addContactDetailsFields () {
			const selectedContactFields = this.selectedContactDetailsFields.map(contactField => ({
				contactFieldId: contactField,
				displayName: this.lookupDataContactFields.find(({ id }) => id === contactField).defaultDisplayName,
				readOnly: contactField === 1,
				mandatory: false
			}))
			this.updateContactDetailFields([
				...this.contactDetailsFields,
				...selectedContactFields
			])
			this.selectedContactDetailsFields = []
			this.searchInput = null
			this.selectAllChecked = false
		},
		updateContactDetailFields (contactDetailsFields) {
			this.$emit('update:contactDetailsFields', contactDetailsFields)
		},
		moveFieldDown (index) {
			this.move(this.contactDetailsFields, index, index + 1)
		},
		moveFieldUp (index) {
			this.move(this.contactDetailsFields, index, index - 1)
		},
		applySort (array) {
			this.updateContactDetailFields(array)
		},
		move (array, from, to) {
			const arrayCopy = JSON.parse(JSON.stringify(array))
			arrayCopy.splice(to, 0, arrayCopy.splice(from, 1)[0])
			return this.applySort(arrayCopy)
		},
		removeContactDetailsField (index) {
			const contactDetailsFields = [...this.contactDetailsFields]
			this.$delete(contactDetailsFields, index)
			this.updateContactDetailFields(contactDetailsFields)
		},
		updateContactDetailsRow (index, property, value) {
			const contactDetailsFields = JSON.parse(JSON.stringify(this.contactDetailsFields))
			contactDetailsFields[index][property] = value
			this.updateContactDetailFields(contactDetailsFields)
		},
		updateAuthenticationDetails (authenticationDetails) {
			this.$emit('update:authenticationDetails', authenticationDetails)
		},
		readOnlyField (field) {
			return field.contactFieldId === 1 // syrenisId currently but this may change
		},
		removeAllExistingFields () {
			this.updateContactDetailFields([])
		},
		restoreDefaultDisplayText (index, field) {
			const contactField = this.lookupDataContactFields.find(({ id }) => id === field.contactFieldId)
			this.updateContactDetailsRow(index, 'displayName', contactField.defaultDisplayName)
		}
	}
}
</script>
